/**
 * 模拟机里的航线存储与传输握手。
 *
 * 单独成模块的原因：**航线上传/下载是一个有游标、有重传、有中途失败的状态机**，
 * 它和"机体在飞"这件事正交。把它和遥测流混在一个类里，两边都会变得难以验证。
 *
 * 内部一律用 `MISSION_ITEM_INT` 的整数表示（纬经度 ×1e7）。
 * 理由是精度：`MISSION_ITEM` 用 float 存纬度，float32 只有约 7 位有效数字，
 * 而纬度需要 9 位（±90.0000000），往返一次就会偏出几米 ——
 * 这是真实存在的经典坑，所以内部只保留无损的那一种表示，float 版本在入口处立刻转换。
 */

/** 一份航线项。字段与 `MISSION_ITEM_INT` 一一对应，不做任何加工。 */
export interface SimMissionItem {
  /** 序号。由存储负责重排，调用方给什么都会被规范化。 */
  seq: number;
  /** `MAV_FRAME` */
  frame: number;
  /** `MAV_CMD` */
  command: number;
  /** 是否是"从这一条开始执行" */
  current: number;
  readonly autocontinue: number;
  readonly param1: number;
  readonly param2: number;
  readonly param3: number;
  readonly param4: number;
  /** 纬度 ×1e7 */
  readonly x: number;
  /** 经度 ×1e7 */
  readonly y: number;
  /** 高度（米，相对 `frame`） */
  readonly z: number;
}

/**
 * 一次上传动作之后，模拟机该做的下一件事。
 *
 * 用返回值而不是回调，是为了让握手逻辑能被直接单测：
 * "上传到一半断线再重来"这类场景在真实链路上极难复现。
 */
export type MissionUploadStep =
  /** 继续索要 `seq` 这一条 */
  | { readonly action: 'request'; readonly seq: number }
  /** 全部收齐，已入库 */
  | { readonly action: 'stored' }
  /** 拒绝（当前没有上传在进行中，或条数超出容量） */
  | { readonly action: 'invalid' };

/**
 * 单条航线的容量上限。
 *
 * 700 是 ArduPilot Copter 的 `MISSION_MAX_NUMBER` 默认值。
 * 超限时**必须**明确拒绝：静默截断会让上传方以为成功，然后飞机执行一条被改过的航线。
 */
export const MAX_MISSION_ITEMS = 700;

/** 未在上传/下载中时用的哨兵值 */
const IDLE = null;

export class SimMission {
  /** 按 `MAV_MISSION_TYPE` 分桶：0=航线 1=围栏 2=返航点 */
  private readonly lists = new Map<number, SimMissionItem[]>();

  private upload: {
    readonly total: number;
    readonly missionType: number;
    readonly received: Map<number, SimMissionItem>;
  } | null = null;

  private download: { readonly missionType: number; cursor: number } | null = null;

  get uploading(): boolean {
    return this.upload !== null;
  }

  get downloading(): boolean {
    return this.download !== null;
  }

  /** 当前正在上传的航线类型，空闲时为 null */
  get uploadMissionType(): number | null {
    return this.upload === null ? null : this.upload.missionType;
  }

  items(missionType = 0): readonly SimMissionItem[] {
    return this.lists.get(missionType) ?? [];
  }

  countOf(missionType = 0): number {
    return this.items(missionType).length;
  }

  /** 预置一份航线（演示与测试用）。序号会被重排成 0..n-1。 */
  load(items: readonly SimMissionItem[], missionType = 0): void {
    this.lists.set(
      missionType,
      items.map((item, index) => ({ ...item, seq: index })),
    );
    this.upload = IDLE;
    this.download = IDLE;
  }

  clear(missionType = 0): void {
    this.lists.set(missionType, []);
    this.upload = IDLE;
    this.download = IDLE;
  }

  clearAll(): void {
    this.lists.clear();
    this.upload = IDLE;
    this.download = IDLE;
  }

  // -------------------------------------------------------------------------
  // 上传：地面站 → 机体
  // -------------------------------------------------------------------------

  beginUpload(total: number, missionType: number): MissionUploadStep {
    if (total > MAX_MISSION_ITEMS) {
      this.upload = IDLE;
      return { action: 'invalid' };
    }

    // 清空一条空航线是合法操作，而且不该进入握手流程：
    // ArduPilot 收到 count=0 就直接落盘并回 ACK。
    if (total === 0) {
      this.lists.set(missionType, []);
      this.upload = IDLE;
      return { action: 'stored' };
    }

    this.upload = { total, missionType, received: new Map() };
    return { action: 'request', seq: 0 };
  }

  /**
   * 收到一条航线项。
   *
   * 接受任意 `seq < total` 的项（不只接受"当前索要的那一条"）：
   * 无线链路下重传很常见，宽容一点能让握手更快收敛，而最终顺序由这里统一重排，
   * 所以乱序到达不会污染结果。
   */
  stageItem(item: SimMissionItem): MissionUploadStep {
    const upload = this.upload;
    if (upload === null) return { action: 'invalid' };
    if (item.seq >= upload.total || item.seq < 0) return this.nextRequest(upload);

    upload.received.set(item.seq, item);
    if (upload.received.size >= upload.total) {
      this.lists.set(
        upload.missionType,
        [...upload.received.values()].sort((a, b) => a.seq - b.seq).map((entry, index) => ({
          ...entry,
          seq: index,
        })),
      );
      this.upload = IDLE;
      return { action: 'stored' };
    }
    return this.nextRequest(upload);
  }

  /** 上传中途放弃（对端断开、GCS 取消） */
  abortUpload(): void {
    this.upload = IDLE;
  }

  private nextRequest(upload: NonNullable<SimMission['upload']>): MissionUploadStep {
    for (let seq = 0; seq < upload.total; seq += 1) {
      if (!upload.received.has(seq)) return { action: 'request', seq };
    }
    // 逻辑上不可达：received.size === total 时已经在上面的分支返回了
    return { action: 'stored' };
  }

  // -------------------------------------------------------------------------
  // 下载：机体 → 地面站
  // -------------------------------------------------------------------------

  beginDownload(missionType: number): number {
    this.download = { missionType, cursor: 0 };
    return this.countOf(missionType);
  }

  /**
   * 取下一条。取不到（序号越界）返回 null，此时下载自动结束 ——
   * 这正是 ArduPilot 的行为：发完最后一条后对端会再要一次或直接 ACK。
   */
  nextDownloadItem(): SimMissionItem | null {
    const download = this.download;
    if (download === null) return null;
    const item = this.items(download.missionType)[download.cursor];
    if (item === undefined) {
      this.download = IDLE;
      return null;
    }
    this.download = { missionType: download.missionType, cursor: download.cursor + 1 };
    return item;
  }

  endDownload(): void {
    this.download = IDLE;
  }
}
